import React, { useState, useEffect } from "react";
import { Typography, DatePicker, Button, Empty, Spin, message, Row, Col, Card, Select, Space } from "antd";
import { Column } from "@ant-design/plots";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getBillingEstimation } from "../../ducks/services";
import moment from 'moment';
const BASE_URL = process.env.REACT_APP_BASE_URL;

const { Text, Title } = Typography;
const { Option } = Select;

// Custom styles
const styles = {
  select: {
    width: 130,
    marginRight: '8px',
    '.ant-select-selector': {
      padding: '0 !important'
    },
    '.ant-select-selection-item': {
      padding: '0 !important',
      lineHeight: '20px !important',
      transition: 'all 0.3s, visibility 0s'
    },
    '.ant-select-selection-placeholder': {
      padding: '0 !important',
      lineHeight: '20px !important',
      transition: 'all 0.3s, visibility 0s'
    }
  },
  monthSelect: {
    width: '280px',
    '.ant-select-selector': {
      padding: '0 !important'
    },
    '.ant-select-selection-item': {
      padding: '0 !important',
      lineHeight: '20px !important',
      transition: 'all 0.3s, visibility 0s'
    },
    '.ant-select-selection-placeholder': {
      padding: '0 !important',
      lineHeight: '20px !important',
      transition: 'all 0.3s, visibility 0s'
    }
  }
};

const index = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 2);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 1);
  const formattedFirstDate = firstDayOfMonth.toISOString().slice(0, 10);
  const formattedLastDate = lastDayOfMonth.toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(formattedFirstDate);
  const [endDate, setEndDate] = useState(formattedLastDate);
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
  const [viewMode, setViewMode] = useState('range'); // 'range' or 'month'
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estimatedData, setEstimatedData] = useState({
    currentEstimate: '14.7 M',
    maa: '125K',
    mau: '98K',
    bandwidth: '1.2 TB',
    newUsers: 14,
    dailyStats: [
      { day: '0', paid_users: 0, trial_users: 0 },
      { day: '5', paid_users: 0, trial_users: 0 },
      { day: '10', paid_users: 2, trial_users: 1 },
      { day: '15', paid_users: 4, trial_users: 2 },
      { day: '20', paid_users: 6, trial_users: 3 },
      { day: '25', paid_users: 8, trial_users: 3 },
      { day: '30', paid_users: 7, trial_users: 3 },
      { day: '35', paid_users: 6, trial_users: 3 }
    ]
  });

  const handleDateChange = (dates, dateStrings) => {
    if (dates) {
      setStartDate(dateStrings[0]);
      setEndDate(dateStrings[1]);
      setLoading(true);
      
      getBillingEstimation(dateStrings[0], dateStrings[1])
        .then((response) => {
          if (response?.data?.header?.code == 200) {
            setData(response?.data?.body?.data);
            if (response?.data?.body?.estimated) {
              setEstimatedData(prev => ({
                ...prev,
                currentEstimate: response.data.body.estimated.invoice || prev.currentEstimate,
                maa: response.data.body.estimated.maa || prev.maa,
                mau: response.data.body.estimated.mau || prev.mau,
                bandwidth: response.data.body.estimated.bandwidth || prev.bandwidth,
                newUsers: response.data.body.estimated.newUsers || prev.newUsers,
                dailyStats: response.data.body.estimated.dailyStats || prev.dailyStats
              }));
            }
          } else {
            message.error(response?.data?.header?.message || "Failed to fetch data");
          }
          setLoading(false);
        })
        .catch((error) => {
          message.error("Something went wrong");
          setLoading(false);
        });
    }
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    const monthStart = moment(value).startOf('month').format('YYYY-MM-DD');
    const monthEnd = moment(value).endOf('month').format('YYYY-MM-DD');
    handleDateChange([moment(monthStart), moment(monthEnd)], [monthStart, monthEnd]);
  };

  const handleViewModeChange = (value) => {
    setViewMode(value);
    if (value === 'month') {
      handleMonthChange(selectedMonth);
    }
  };

  // Generate last 12 months options
  const getMonthOptions = () => {
    const options = [];
    for (let i = 0; i < 12; i++) {
      const monthDate = moment().subtract(i, 'months');
      options.push({
        value: monthDate.format('YYYY-MM'),
        label: monthDate.format('MMMM YYYY')
      });
    }
    return options;
  };

  useEffect(() => {
    // Initial data fetch
    handleDateChange(
      [moment(startDate), moment(endDate)],
      [startDate, endDate]
    );
  }, []);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '0' }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const config = {
    data,
    isGroup: true,
    xField: "month",
    yField: "total_cost_of_accounts",
    seriesField: "name",
    label: {
      position: "middle",
      layout: [
        { type: "interval-adjust-position" },
        { type: "interval-hide-overlap" },
        { type: "adjust-color" },
      ],
    },
  };

  const StatCard = ({ title, value }) => (
    <Card 
      style={{ 
        background: '#f8f9fe', 
        borderRadius: '12px',
        border: '1px solid #e6e8f0',
        marginBottom: '16px'
      }}
      bodyStyle={{ padding: '16px' }}
    >
      <Text style={{ 
        display: 'block', 
        color: '#6b7280', 
        marginBottom: '4px',
        fontSize: '14px'
      }}>
        {title}
      </Text>
      <Title level={3} style={{ 
        margin: 0, 
        color: '#111827',
        fontSize: '24px'
      }}>
        {value}
      </Title>
    </Card>
  );

  return (
    <div className="col-design col-design3">
      <Row 
        justify="space-between" 
        align="middle" 
        style={{ 
          marginBottom: '24px', 
          padding: '12px 16px',
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)'
        }}
      >
        <Space size="middle" align="center">
          <Text strong style={{ fontSize: '14px' }}>View By:</Text>
          <Select 
            defaultValue="range"
            value={viewMode}
            onChange={handleViewModeChange}
            style={{
              width: 130,
              marginRight: '8px'
            }}
          >
            <Option value="range">Date Range</Option>
            <Option value="month">Month</Option>
          </Select>
        </Space>

        <Space size="middle" align="center">
          {viewMode === 'range' ? (
            <DatePicker.RangePicker 
              onChange={handleDateChange}
              defaultValue={[moment(startDate), moment(endDate)]}
              format="YYYY-MM-DD"
              style={{
                width: '280px'
              }}
            />
          ) : (
            <Select
              style={{
                width: '280px'
              }}
              value={selectedMonth}
              onChange={handleMonthChange}
              options={getMonthOptions()}
              dropdownStyle={{
                maxHeight: '400px',
                overflowY: 'auto'
              }}
            />
          )}
        </Space>
      </Row>

      {loading ? (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={24}>
            <Col xs={24} lg={8}>
              <div style={{ 
                background: '#fff',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
                height: '100%'
              }}>
                <StatCard title="Estimated Invoice" value={`$${estimatedData.currentEstimate}`} />
                <StatCard title="MAA" value={estimatedData.maa} />
                <StatCard title="MAU" value={estimatedData.mau} />
                <StatCard title="Bandwidth" value={estimatedData.bandwidth} />
              </div>
            </Col>
            
            <Col xs={24} lg={16}>
              <div style={{ 
                background: '#fff',
                padding: '24px',
                borderRadius: '8px',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
                height: '100%',
                minHeight: '500px'
              }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={estimatedData.dailyStats}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="day" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280' }}
                      label={{ 
                        value: viewMode === 'month' ? moment(selectedMonth).format('MMMM YYYY') : 'Days',
                        position: 'bottom', 
                        offset: -15,
                        fill: '#6b7280'
                      }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280' }}
                      label={{ 
                        value: '$ Cost', 
                        angle: -90, 
                        position: 'insideLeft',
                        offset: -5,
                        fill: '#6b7280'
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ 
                        paddingTop: '20px'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="paid_users"
                      name="Paid Users"
                      stroke="#6366f1"
                      fill="#818cf8"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="trial_users"
                      name="Trial Users"
                      stroke="#60a5fa"
                      fill="#93c5fd"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>

          {data && data.length > 0 && (
            <div style={{ 
              marginTop: '24px',
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)'
            }}>
              <Typography style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '14px' }}>Active Account</Text>
              </Typography>
              <Column {...config} style={{ padding: "15px" }} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Add global styles
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  .custom-select .ant-select-selector {
    padding: 0 !important;
  }
  .custom-select .ant-select-selection-item,
  .custom-select .ant-select-selection-placeholder {
    padding: 0 !important;
    line-height: 20px !important;
    transition: all 0.3s, visibility 0s !important;
  }
`;
document.head.appendChild(styleSheet);

export default index;
